<template>
  <section v-if="ownerMode" class="owner-management">
    <div class="owner-management__header">
      <div>
        <p class="eyebrow">TENANT OWNER SECURITY</p>
        <h2>契約企業管理者</h2>
        <p>
          自社の最上位管理者を追加・交代します。通常の役割編集からは追加・解除できません。
        </p>
      </div>
      <CButton color="primary" :disabled="loading || saving" @click="openInvite">
        管理者を招待
      </CButton>
    </div>

    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>
    <CAlert color="warning">
      最後の1人は解除できません。交代する場合は、新しい管理者が招待を受諾した後に現在の管理者を解除してください。
    </CAlert>

    <div v-if="loading" class="text-center py-4"><CSpinner color="primary"/></div>
    <template v-else>
      <CCard class="owner-card">
        <CCardHeader><strong>現在の契約企業管理者（{{ activeOwners.length }}名）</strong></CCardHeader>
        <CCardBody>
          <div class="owner-grid">
            <article v-for="owner in activeOwners" :key="owner.userId" class="owner-person">
              <div>
                <strong>{{ owner.name }}</strong>
                <span>{{ owner.email }}</span>
                <small>{{ owner.employeeId || '社員番号未設定' }}</small>
              </div>
              <CButton
                color="danger"
                variant="outline"
                :disabled="saving || activeOwners.length <= 1"
                @click="openRemoval(owner)"
              >
                管理者を解除
              </CButton>
            </article>
          </div>
        </CCardBody>
      </CCard>

      <CCard class="owner-card">
        <CCardHeader><strong>追加管理者の招待履歴</strong></CCardHeader>
        <CCardBody>
          <div v-if="!normalizedInvitations.length" class="empty-state">
            追加管理者の招待はまだありません。
          </div>
          <div v-else class="invitation-list">
            <article v-for="invitation in normalizedInvitations" :key="invitation.invitationId" class="invitation-row">
              <div>
                <strong>{{ invitation.invitedName }}</strong>
                <span>{{ invitation.maskedEmail }}</span>
                <small>
                  {{ invitation.typeLabel }}・期限 {{ formatDateTime(invitation.expiresAt) }}
                </small>
              </div>
              <div class="invitation-row__actions">
                <CBadge :color="statusColor(invitation.status)">
                  {{ statusLabel(invitation.status) }}
                </CBadge>
                <CButton
                  v-if="canResend(invitation)"
                  size="sm"
                  color="primary"
                  variant="outline"
                  :disabled="saving"
                  @click="resend(invitation)"
                >
                  再送
                </CButton>
                <CButton
                  v-if="canRevoke(invitation)"
                  size="sm"
                  color="danger"
                  variant="outline"
                  :disabled="saving"
                  @click="revoke(invitation)"
                >
                  取消
                </CButton>
              </div>
            </article>
          </div>
        </CCardBody>
      </CCard>
    </template>

    <CModal
      title="契約企業管理者を招待"
      :show.sync="inviteModal"
      :close-on-backdrop="false"
      size="lg"
    >
      <CAlert color="info">
        招待された本人がメールリンクを開き、本人確認を完了した時点で管理者になります。<br>
        下のパスワードは、現在ログイン中のあなた自身を再認証するためのものです。招待される方のパスワードは入力しません。
      </CAlert>
      <div class="invite-type">
        <label>
          <input type="radio" value="existing" v-model="inviteForm.type">
          <span>登録済みの自社利用者</span>
        </label>
        <label>
          <input type="radio" value="new" v-model="inviteForm.type">
          <span>新しい利用者</span>
        </label>
      </div>

      <!--
        **必須の印は自分で書く。**CInputのlabel属性には印を入れられないので、
        label属性を外して<label>を並べている。ここは印が1つも無く、
        どれを埋めれば送れるのか押すまで分からなかった。
      -->
      <template v-if="inviteForm.type === 'existing'">
        <label>招待する利用者<span class="asterisk">*</span></label>
        <!--
          **CSelectはinputを出さないので、v-modelでは結べない。**
          CoreUI 3のCSelectが出すのはupdate:valueだけで、v-modelが既定で
          待っているinputは出ない（CInputとCTextareaは両方出すので、
          あちらはv-modelのままで動く）。v-modelで結ぶと**選んでも何も
          入らない**ので、下のinviteProblemが返す断りを画面からは永久に
          消せない。理由が読めるようになっても、その理由が消せない。

          結び直すと、選んだ人が見えている状態と送る値も揃う。結べて
          いなかったあいだは中身が空のまま動かないので、閉じて開いても
          <option>が塗り直されず、**前に選んだ人が見えたままで中身は空**
          になっていた。
        -->
        <CSelect
          :options="candidateOptions"
          :value="inviteForm.targetUserId"
          @update:value="chooseCandidate"
        />
      </template>
      <template v-else>
        <label>氏名<span class="asterisk">*</span></label>
        <CInput maxlength="100" v-model.trim="inviteForm.invitedName"/>
        <label>メールアドレス<span class="asterisk">*</span></label>
        <CInput type="email" maxlength="254" v-model.trim="inviteForm.email"/>
        <label>社員番号<span class="asterisk">*</span></label>
        <CInput maxlength="20" v-model.trim="inviteForm.employeeId"/>
      </template>

      <label>ご自身（操作中の方）のパスワード<span class="asterisk">*</span></label>
      <CInput
        type="password"
        autocomplete="current-password"
        v-model="inviteForm.currentPassword"
        description="招待する方ではなく、操作中の契約企業管理者本人を再認証します。"
      />
      <!--
        **長さの決まりはラベルにも出す。**descriptionは入力欄の下にあり、
        書き終えてから気付くことになる。断られる条件は書き始める前に見せる。
      -->
      <label>招待理由（10文字以上）<span class="asterisk">*</span></label>
      <CTextarea
        rows="4"
        maxlength="500"
        v-model.trim="inviteForm.reason"
        description="10文字以上500文字以内。監査ログへ記録します。"
      />

      <!--
        **押せなかった理由は、モーダルの中に出す。**
        画面上端のCAlertは、.modal(1050)と.modal-backdrop(1040)の下に潜る。
        モーダルは成功時にしか閉じないので、以前はパスワードだけが消えて
        何も起きていないように見えていた。
      -->
      <CAlert v-if="inviteError" color="danger" class="mb-0">
        {{ inviteError }}
      </CAlert>

      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeInvite">キャンセル</CButton>
          <CButton color="primary" :disabled="saving" @click="submitInvite">
            {{ saving ? '処理しています' : 'あなたを再認証して招待' }}
          </CButton>
        </div>
      </template>
    </CModal>

    <CModal
      title="契約企業管理者を解除"
      :show.sync="removalModal"
      :close-on-backdrop="false"
    >
      <template v-if="selectedOwner">
        <CAlert color="warning">
          {{ selectedOwner.name }}さんの最上位管理者Roleを解除します。利用者アカウント自体は削除されません。
        </CAlert>
        <label>ご自身（操作中の方）のパスワード<span class="asterisk">*</span></label>
        <CInput
          type="password"
          autocomplete="current-password"
          v-model="removalForm.currentPassword"
          description="解除対象者のパスワードではなく、操作しているご自身を、もう一度確認します。"
        />
        <label>解除理由（10文字以上）<span class="asterisk">*</span></label>
        <CTextarea
          rows="4"
          maxlength="500"
          v-model.trim="removalForm.reason"
          description="10文字以上500文字以内。監査ログへ記録します。"
        />
      </template>
      <!-- 招待と同じ理由で、断りの文言はモーダルの中へ出す。 -->
      <CAlert v-if="removalError" color="danger" class="mb-0">
        {{ removalError }}
      </CAlert>
      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" :disabled="saving" @click="closeRemoval">キャンセル</CButton>
          <CButton color="danger" :disabled="saving" @click="submitRemoval">
            {{ saving ? '処理しています' : '再認証して解除' }}
          </CButton>
        </div>
      </template>
    </CModal>
  </section>
</template>

<script>
import { getAccessContext } from '@/services/accessContext'
import api from '@/services/tenantAccessManagement'
import { isTenantOwner } from '@/utils/accessPolicy'

const emptyInviteForm = () => ({
  type: 'existing',
  targetUserId: '',
  invitedName: '',
  email: '',
  employeeId: '',
  currentPassword: '',
  reason: ''
})

export default {
  name: 'TenantOwnerManagement',
  data () {
    return {
      ownerMode: false,
      loading: false,
      saving: false,
      users: [],
      invitations: [],
      inviteModal: false,
      removalModal: false,
      selectedOwner: null,
      inviteForm: emptyInviteForm(),
      removalForm: { currentPassword: '', reason: '' },
      successMessage: '',
      errorMessage: '',
      /*
       * **モーダルの中で出す文言は、画面上端のものと分けて持つ。**
       * errorMessageを出すCAlertはページの先頭にあり、モーダルが開いている
       * 間は暗幕の下で読めない。招待・解除はモーダルを開いたまま断られる
       * ので、それぞれ専用の入れ物を持たせる。
       */
      inviteError: '',
      removalError: ''
    }
  },
  computed: {
    normalizedUsers () {
      return this.users.map(item => ({
        userId: Number(this.value(item, 'userId', 'user_id')),
        name: this.value(item, 'name') || '氏名未設定',
        email: this.value(item, 'email') || 'メールアドレス未設定',
        employeeId: this.value(item, 'employeeId', 'employee_id'),
        roleCodes: this.arrayValue(item, 'roleCodes', 'role_codes')
      })).filter(item => Number.isFinite(item.userId) && item.userId > 0)
    },
    activeOwners () {
      return this.normalizedUsers.filter(item => item.roleCodes.includes('TENANT_OWNER'))
    },
    candidates () {
      return this.normalizedUsers.filter(item => !item.roleCodes.includes('TENANT_OWNER'))
    },
    candidateOptions () {
      return [{ value: '', label: '利用者を選択' }].concat(this.candidates.map(item => ({
        value: String(item.userId),
        label: `${item.name}（${item.email}）`
      })))
    },
    normalizedInvitations () {
      return this.invitations.map(item => ({
        invitationId: Number(this.value(item, 'invitationId', 'invitation_id')),
        invitedName: this.value(item, 'invitedName', 'invited_name') || '氏名未設定',
        maskedEmail: this.value(item, 'maskedEmail', 'masked_email') || 'メール未設定',
        invitationType: this.value(item, 'invitationType', 'invitation_type'),
        typeLabel: this.value(item, 'invitationType', 'invitation_type') === 'TENANT_OWNER_EXISTING'
          ? '登録済み利用者' : '新しい利用者',
        status: String(this.value(item, 'status') || ''),
        expiresAt: this.value(item, 'expiresAt', 'expires_at')
      })).filter(item => Number.isFinite(item.invitationId))
    }
  },
  watch: {
    inviteModal (visible) {
      if (!visible) {
        this.inviteForm.currentPassword = ''
        /* 前回の断りの文言を、次に開いたときへ持ち越さない。 */
        this.inviteError = ''
      }
    },
    removalModal (visible) {
      if (!visible) {
        this.removalForm.currentPassword = ''
        this.removalError = ''
      }
    }
  },
  async created () {
    try {
      const context = await getAccessContext()
      this.ownerMode = isTenantOwner(context)
      if (this.ownerMode) await this.load()
    } catch (error) {
      this.errorMessage = this.errorFrom(error, '管理者情報を確認できませんでした。')
    }
  },
  methods: {
    value (item, ...keys) {
      for (const key of keys) {
        if (item && Object.prototype.hasOwnProperty.call(item, key)) return item[key]
        const upper = key.toUpperCase()
        if (item && Object.prototype.hasOwnProperty.call(item, upper)) return item[upper]
      }
      return null
    },
    arrayValue (item, ...keys) {
      const value = this.value(item, ...keys)
      if (Array.isArray(value)) return value.map(item => String(item))
      if (typeof value === 'string') {
        return value.replace(/[{}]/g, '').split(',').map(item => item.trim()).filter(Boolean)
      }
      return []
    },
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        const [users, invitations] = await Promise.all([
          api.getOwnerUsers(),
          api.getOwnerInvitations()
        ])
        if (!Array.isArray(users) || !Array.isArray(invitations)) {
          throw new Error('契約企業管理者の情報を読み取れませんでした。しばらく経ってからもう一度お試しください。')
        }
        this.users = users
        this.invitations = invitations
      } catch (error) {
        this.errorMessage = this.errorFrom(error, '契約企業管理者の情報を取得できませんでした。')
      } finally {
        this.loading = false
      }
    },
    openInvite () {
      this.inviteForm = emptyInviteForm()
      this.inviteError = ''
      this.inviteModal = true
    },
    /**
     * 選ばれた利用者を受け取る。
     *
     * **CSelectは値ではなく選択肢そのものを返してくることがある。**
     * CoreUIが返しているのは `option.value || option` で、「利用者を選択」の
     * ようにvalueが空文字の選択肢では、その選択肢（オブジェクト）が返る。
     * そのまま入れると「選ばれている」と誤って判断してしまい、
     * Number()がNaNになった招待をサーバへ送ることになる。
     */
    chooseCandidate (chosen) {
      const value = (chosen && typeof chosen === 'object') ? chosen.value : chosen
      this.inviteForm.targetUserId = (value === null || value === undefined) ? '' : String(value)
    },
    closeInvite () {
      this.inviteForm.currentPassword = ''
      this.inviteError = ''
      this.inviteModal = false
    },
    /**
     * サーバが再認証そのものを断ったか。
     *
     * パスワードが一致しないときだけ403が返る。**入れ直してもらう必要が
     * あるのはこのときだけ**で、通信断や他の失敗でも消すと、押すたびに
     * 入力し直しになる。
     */
    reauthenticationRefused (error) {
      const status = error && error.response && error.response.status
      return status === 401 || status === 403
    },
    /**
     * 招待の入力不備を、送る前に文言で返す。不備が無ければ空文字。
     *
     * **例外にしてcatchで受けない。**catchにはサーバが再認証を断ったときの
     * 後始末（パスワードを消す）が入っている。入力不備でそこまで走ると、
     * 押すたびに入れたパスワードだけが消えて、何も起きていないように見える。
     */
    inviteProblem () {
      const form = this.inviteForm
      if (form.type === 'existing' && !form.targetUserId) {
        return '招待する利用者を選択してください。'
      }
      if (form.type === 'new') {
        if (!form.invitedName) return '氏名を入力してください。'
        if (!form.email) return 'メールアドレスを入力してください。'
        if (!form.employeeId) return '社員番号を入力してください。'
      }
      if (!form.currentPassword) {
        return 'ご自身（操作中の方）のパスワードを入力してください。'
      }
      if (!form.reason || form.reason.length < 10 || form.reason.length > 500) {
        return '招待理由は10文字以上500文字以内で入力してください。'
      }
      return ''
    },
    async submitInvite () {
      /* 入力不備はここで返す。入れたものは何も消さない。 */
      const problem = this.inviteProblem()
      if (problem) {
        this.inviteError = problem
        return
      }
      this.saving = true
      this.inviteError = ''
      this.errorMessage = ''
      try {
        const existing = this.inviteForm.type === 'existing'
        const payload = {
          targetUserId: existing ? Number(this.inviteForm.targetUserId) : null,
          invitedName: existing ? null : this.inviteForm.invitedName,
          email: existing ? null : this.inviteForm.email,
          employeeId: existing ? null : this.inviteForm.employeeId,
          currentPassword: this.inviteForm.currentPassword,
          reason: this.inviteForm.reason
        }
        const response = await api.inviteOwner(payload)
        this.successMessage = (response && response.message) || '追加の契約企業管理者を招待しました。'
        this.closeInvite()
        await this.load()
      } catch (error) {
        if (this.reauthenticationRefused(error)) {
          this.inviteForm.currentPassword = ''
        }
        this.inviteError = this.errorFrom(error, '契約企業管理者を招待できませんでした。')
      } finally {
        this.saving = false
      }
    },
    async resend (invitation) {
      if (!window.confirm(`${invitation.invitedName}さんへ新しい招待リンクを再送しますか？`)) return
      await this.runInvitationAction(
        () => api.resendOwnerInvitation(invitation.invitationId),
        '招待を再送しました。',
        '招待を再送できませんでした。'
      )
    },
    async revoke (invitation) {
      if (!window.confirm(`${invitation.invitedName}さんへの招待を取り消しますか？`)) return
      await this.runInvitationAction(
        () => api.revokeOwnerInvitation(invitation.invitationId),
        '招待を取り消しました。',
        '招待を取り消せませんでした。'
      )
    },
    async runInvitationAction (action, success, failure) {
      this.saving = true
      this.errorMessage = ''
      try {
        const response = await action()
        this.successMessage = (response && response.message) || success
        await this.load()
      } catch (error) {
        this.errorMessage = this.errorFrom(error, failure)
      } finally {
        this.saving = false
      }
    },
    openRemoval (owner) {
      if (this.activeOwners.length <= 1) return
      this.selectedOwner = owner
      this.removalForm = { currentPassword: '', reason: '' }
      this.removalError = ''
      this.removalModal = true
    },
    closeRemoval () {
      this.removalForm.currentPassword = ''
      this.removalError = ''
      this.removalModal = false
      this.selectedOwner = null
    },
    /** 解除の入力不備を、送る前に文言で返す。理由は招待と同じ。 */
    removalProblem () {
      const form = this.removalForm
      if (!form.currentPassword) {
        return 'ご自身（操作中の方）のパスワードを入力してください。'
      }
      if (!form.reason || form.reason.length < 10 || form.reason.length > 500) {
        return '解除理由は10文字以上500文字以内で入力してください。'
      }
      return ''
    },
    async submitRemoval () {
      if (!this.selectedOwner) return
      const problem = this.removalProblem()
      if (problem) {
        this.removalError = problem
        return
      }
      this.saving = true
      this.removalError = ''
      this.errorMessage = ''
      try {
        await api.removeOwner(this.selectedOwner.userId, {
          currentPassword: this.removalForm.currentPassword,
          reason: this.removalForm.reason
        })
        this.successMessage = `${this.selectedOwner.name}さんの契約企業管理者Roleを解除しました。`
        this.closeRemoval()
        await this.load()
      } catch (error) {
        if (this.reauthenticationRefused(error)) {
          this.removalForm.currentPassword = ''
        }
        this.removalError = this.errorFrom(error, '契約企業管理者を解除できませんでした。')
      } finally {
        this.saving = false
      }
    },
    canResend (invitation) {
      return ['PENDING', 'EMAIL_SEND_FAILED', 'EXPIRED'].includes(invitation.status)
    },
    canRevoke (invitation) {
      return ['PENDING', 'EMAIL_SEND_FAILED'].includes(invitation.status)
    },
    statusLabel (status) {
      return {
        PENDING: '受諾待ち',
        EMAIL_SEND_FAILED: 'メール未送信',
        EXPIRED: '期限切れ',
        ACCEPTED: '受諾済み',
        REVOKED: '取消済み'
      }[status] || status || '不明'
    },
    statusColor (status) {
      return {
        PENDING: 'warning',
        EMAIL_SEND_FAILED: 'danger',
        EXPIRED: 'secondary',
        ACCEPTED: 'success',
        REVOKED: 'dark'
      }[status] || 'secondary'
    },
    formatDateTime (value) {
      if (!value) return '未設定'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      }).format(date)
    },
    errorFrom (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data) return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
.owner-management { margin: 22px 0; }
.owner-management__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 18px;
}
.owner-management__header h2 { margin: 0; color: #17394d; }
.owner-management__header p:last-child { margin: 6px 0 0; color: #667e8c; }
.eyebrow { margin: 0 0 5px; color: #0872b5; font-size: 10px; font-weight: 900; letter-spacing: .12em; }
.owner-card { margin-bottom: 16px; }
.owner-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.owner-person,
.invitation-row {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border: 1px solid #dfe8ed;
  border-radius: 12px;
  gap: 14px;
}
.owner-person > div,
.invitation-row > div:first-child { display: grid; min-width: 0; gap: 3px; }
.owner-person span,
.invitation-row span { color: #58717f; overflow-wrap: anywhere; }
.owner-person small,
.invitation-row small { color: #81919a; }
.invitation-list { display: grid; gap: 10px; }
.invitation-row__actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.invite-type { display: grid; margin-bottom: 18px; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.invite-type label {
  display: flex;
  min-height: 48px;
  align-items: center;
  padding: 12px;
  border: 1px solid #dce6eb;
  border-radius: 10px;
  gap: 9px;
}
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
/*
  ラベルの見た目はCoreUIの既定のままにする。ここでlabel全体へ書くと、
  .invite-type の選択肢（同じlabel要素）まで一緒に変わってしまう。
*/
.asterisk { margin-left: 3px; color: #d33d4c; }
.empty-state { padding: 22px; color: #718793; text-align: center; }

@media (max-width: 767.98px) {
  .owner-management__header { flex-direction: column; }
  .owner-management__header .btn { width: 100%; min-height: 46px; }
  .owner-grid { grid-template-columns: minmax(0, 1fr); }
  .owner-person,
  .invitation-row { align-items: stretch; flex-direction: column; }
  .owner-person .btn { width: 100%; min-height: 44px; }
  .invitation-row__actions { justify-content: flex-start; flex-wrap: wrap; }
  .invite-type { grid-template-columns: minmax(0, 1fr); }
}
</style>
